import { traverse } from './hierarchy/traverse';
import { HierarchyFilter } from './hierarchy/HierarchyFilter';
import { AssetTypes } from 'constants/alo';
import { buildTree } from './hierarchy/buildTree';

export { buildTree } from './hierarchy/buildTree';
export { clone } from './hierarchy/hierarchyClone';
export { HierarchyFilter }
export { HierarchyPrune } from './hierarchy/hierarchyPrune';
export { hierarchySort as sort } from './hierarchy/hierarchySort';
export { traverse };
export { icon } from './hierarchy/icon';

export const getParentNode = (node: Hierarchy.IHierarchyItem, assetType: string) => {
    while (!!node) {
        if (node.category === assetType) {
            return node;
        }
        node = node.parent;
    }
    return null;
}

export const getClient = (item: Hierarchy.IHierarchyItem) => {
    return getParentNode(item, AssetTypes.Client);
}

export const getCountry = (item: Hierarchy.IHierarchyItem) => {
    return getParentNode(item, AssetTypes.Country);
}

export const getPad = (item: Hierarchy.IHierarchyItem) => {
    return getParentNode(item, AssetTypes.Battery);
}

export const getField = (item: Hierarchy.IHierarchyItem) => {
    return getParentNode(item, AssetTypes.Field);
}

export const EmptyFilter: Hierarchy.Filter = {
    name: '',
    online: false,
    offline: false,
    hibernate: false,
    serviceLevel: null,
    wellType: null
};

export const formatLocationString = (item: Hierarchy.IHierarchyItem): string => {
    let locations = [];

    if (!item) {
        return '';
    }

    while (item && item.id) {
        locations.push(item.name);
        item = item.parent;
    }

    locations = locations.reverse();

    return locations.join(' / ');
}

export const parents = (item: Hierarchy.IHierarchyItem, cb: (parent: Hierarchy.IHierarchyItem) => void) => {
    if (!item) {
        return;
    }

    let parent = item; //item.parent;

    while (parent && parent.id) {
        cb(parent);
        parent = parent.parent;
    }
};

export const expand = (item: Hierarchy.IHierarchyItem) => {
    if (!item) {
        return;
    }
    traverse([item], (child) => {
        child.expanded = true;
    });

    parents(item, (parent) => {
        parent.expanded = true;
    });
};

export const expandChildren = (node: Hierarchy.IHierarchyItem, onExpanded?: (node: Hierarchy.IHierarchyItem) => void): void => {
    traverse([node], (child) => {
        if (!child.expanded) {
            child.expanded = true;
            if (onExpanded) {
                onExpanded(child);
            }
        }
    });
}

export const selectChildren = (
    node: Hierarchy.IHierarchyItem,
    select: boolean,
    cb: (nodes: Hierarchy.IHierarchyItem[]) => void
): void => {
    const nodes: Hierarchy.IHierarchyItem[] = [];

    traverse(node.children, (child) => {

        if (child.visible) {
            child.selected = select;
        }

        if (!child.expanded) {
            child.expanded = true;
        }

        if (select) {

            if (child.visible) {
                nodes.push(child);
            }

        } else {
            nodes.push(child);
        }

    });

    cb(nodes);
}

export const selectNode = (map: HierarchyMap, node: Hierarchy.IHierarchyItem, select: boolean): Hierarchy.IHierarchyItem[] => {

    const nodes: Hierarchy.IHierarchyItem[] = [];

    node.selected = select;

    nodes.push(node);

    if (node.selected && !node.expanded) {
        node.expanded = true;
    }

    // toggle node + children
    selectChildren(node, select, children => {
        nodes.push(...children);
    });

    // handle parent nodes as well
    if (select) {
        trySelectParent(map, node.parent, nodes);
    } else {
        unselectParent(map, node, nodes);
    }

    var unique = new Map(nodes.map(item => [item.id, item]));//.values();
    var result = [];

    unique.forEach(node => {
        result.push(node);
    });
    
    return result;
}

const trySelectParent = (map: HierarchyMap, parent: Hierarchy.IHierarchyItem, arr: Hierarchy.IHierarchyItem[]): void => {
    if (!parent || !parent.id) {
        return;
    }

    const children = parent.children || [];

    const selected = children.filter((child) => {
        return map[child.id].selected;
    });

    if (selected.length && selected.length === children.length) {
        parent.selected = true;
        arr.push(parent);
        trySelectParent(map, parent.parent, arr);
    }

}

const unselectParent = (map: HierarchyMap, node: Hierarchy.IHierarchyItem, unselected: Hierarchy.IHierarchyItem[]): void => {

    node.selected = allChildrenSelected(node);

    for (let key in map) {
        let item = map[key];
        if (node.parent && item.id === node.parent.id) {
            unselected.push(item);
            unselectParent(map, item, unselected);
        }
    }
}

const allChildrenSelected = (node: Hierarchy.IHierarchyItem): boolean => {

    let selected = 0;
    let partiallySelected = 0;
    let count = 0;

    traverse([node], (child) => {
        count++;

        if (child.selected) {
            selected++;
        }

        if (child.selected === null) {
            partiallySelected++;
        }
    });

    if (selected === count) {
        // all children selected
        return true;
    } else if (selected === 0 && partiallySelected === 0) {
        // no children selected
        return false;
    }

    // some children are selected
    return null;
}

export const filter = (filter: Hierarchy.Filter, hierarchy: Hierarchy.IHierarchyItem[], selectedWells: HierarchyMap) => {

    const result: Hierarchy.FilterResult = {
        hierarchy: null,
        hierarchyMap: {},
        selectedWells: null,
        unselectedWells: null
    };

    const hierarchyMap: HierarchyMap = {};
    traverse(hierarchy, node => hierarchyMap[node.id] = node);

    const { name, online, offline, hibernate, serviceLevel, wellType } = filter;
    const wellMap: { [itemId: string]: Hierarchy.IHierarchyItem } = {};

    // this is wrong....need to filter by name here and build wellMap after the tree has been built

    if (name) {

        let matchedByName = Object.values(hierarchyMap).filter(node => node.name.toLowerCase().includes(name.toLowerCase()));

        if (!matchedByName.length) {
            Toast('Search produced no results', 'info');
            return;
        }

        const findWellForNode = (node: Hierarchy.IHierarchyItem) => {
            traverse([node], item => {
                if (item.isWell && !wellMap[item.id]) {
                    wellMap[item.id] = item;
                }
            })
        };

        matchedByName.forEach(node => {
            findWellForNode(node);
        });

    } else {

        Object.values(hierarchyMap).filter(node => node.isWell).forEach(well => wellMap[well.id] = well);

    }


    // filter the rest here
    const dataTransmissionStatuses = [];

    if (online) {
        dataTransmissionStatuses.push('ONLINE');
    }

    if (offline) {
        dataTransmissionStatuses.push('OFFLINE');
    }

    if (hibernate) {
        dataTransmissionStatuses.push('HIBERNATE');
        dataTransmissionStatuses.push('HIBERNATING');
    }

    let wells = Object.values(wellMap);

    const wellFilter = (node: Hierarchy.IHierarchyItem) => {

        let result = true;

        if (dataTransmissionStatuses.length) {
            result = result && dataTransmissionStatuses.includes(node.transStatus);
        }

        if (serviceLevel) {
            if (serviceLevel === 2) {
                result = result && (node.serviceLevel === serviceLevel || !node.serviceLevel); // default unspecified service levels to 2
            } else {
                result = result && node.serviceLevel === serviceLevel;
            }
        }

        if (wellType) {

            switch (wellType) {
                case 'ION':
                    result = result && node.locationId === null;
                    break;
                case 'Zedi':
                    result = result && node.locationId > 0;
                    break;
                case 'Pi':
                    result = result && node.locationId === -1;
                    break;
            }

        }


        return result;
    };

    const matched = wells.filter(wellFilter);

    // build a new tree from the matched nodes
    result.hierarchy = buildTree(matched);

    // rebuild hierarchy map
    traverse(result.hierarchy, (node) => {
        result.hierarchyMap[node.id] = node;
    });

    // THEN

    // Filter may have forced some wells to be unselected
    const unselectedWells = {};
    let updateSelectedWells = false;

    for (const itemId in selectedWells) {

        if (!result.hierarchyMap[itemId]) {
            // update state to reflect that wells have been unselected
            // but we still need to notify the client in case their
            // is additional work that needs to be done (like unsubscribing
            // from realtime data)
            selectedWells[itemId].selected = false;
            unselectedWells[itemId] = selectedWells[itemId];
            delete selectedWells[itemId];
            updateSelectedWells = true;
        }

    }

    if (updateSelectedWells) {
        result.selectedWells = selectedWells;
        result.unselectedWells = unselectedWells;
    }

    return { filter, result };
}

export const search = (term: string, hierarchy: Hierarchy.IHierarchyItem[]) => {
    if (term && term.length) {
        const hierarchyFilter: HierarchyFilter = new HierarchyFilter(hierarchy);

        hierarchyFilter.filter(term, (item: Hierarchy.IHierarchyItem) => {
            item.expanded = true;
        });
    } else {
        traverse(hierarchy, (node) => {
            node.visible = true;
            node.expanded = false;
        });
    }
};